import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const FinancialSummaryCard = ({ 
  icon, 
  label, 
  value, 
  description, 
  variant = 'default',
  isEditable = false,
  onEdit,
  editField,
  currentValue,
  inputType = 'number',
  prefix = '',
  suffix = ''
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValue, setEditValue] = useState(currentValue || '');
  const [error, setError] = useState('');

  const variantStyles = {
    default: 'bg-card border-border',
    primary: 'bg-primary/5 border-primary/20',
    accent: 'bg-accent/5 border-accent/20'
  };

  const handleEditClick = () => {
    setEditValue(currentValue || '');
    setError('');
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setEditValue(currentValue || '');
    setError('');
    setIsEditMode(false);
  };

  const validateInput = (val) => {
    const numValue = parseFloat(val);
    
    if (!val || val === '') {
      return 'This field is required';
    }
    
    if (isNaN(numValue)) {
      return 'Please enter a valid number';
    }

    // Field-specific validation
    if (editField === 'currentAge') {
      if (numValue < 18 || numValue > 100) {
        return 'Age must be between 18 and 100';
      }
    } else if (editField === 'monthlyIncome') {
      if (numValue <= 0) {
        return 'Monthly income must be greater than 0';
      }
    } else if (editField === 'preRetirementInflation' || editField === 'postRetirementInflation') {
      if (numValue < 0 || numValue > 100) {
        return 'Inflation rate must be between 0 and 100';
      }
    } else if (editField === 'retirementAge') {
      // Get current age from the parent component's userData
      // We'll need to pass it as a prop, but for now we'll use a workaround
      // by accessing it from localStorage
      try {
        const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        const calculateCurrentAge = (dobString) => {
          if (!dobString) return 25;
          const [day, month, year] = dobString?.split('/')?.map(Number);
          const birthDate = new Date(year, month - 1, day);
          const today = new Date();
          let age = today?.getFullYear() - birthDate?.getFullYear();
          const monthDiff = today?.getMonth() - birthDate?.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
            age--;
          }
          return age;
        };
        const currentAge = userProfile?.dateOfBirth ? calculateCurrentAge(userProfile?.dateOfBirth) : 25;
        const minRetirementAge = Math.max(25, currentAge);
        
        if (numValue <= minRetirementAge) {
          return `Retirement age must be greater than ${minRetirementAge} years`;
        }
        if (numValue > 100) {
          return 'Retirement age cannot exceed 100 years';
        }
      } catch (error) {
        console.error('Error validating retirement age:', error);
        if (numValue <= 25) {
          return 'Retirement age must be greater than 25 years';
        }
      }
    }

    return '';
  };

  const handleSave = () => {
    const validationError = validateInput(editValue);
    
    if (validationError) {
      setError(validationError);
      return;
    }

    const numValue = parseFloat(editValue);
    onEdit?.(editField, numValue);
    setIsEditMode(false);
    setError('');
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSave();
    } else if (e?.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className={`${variantStyles?.[variant]} border rounded-lg p-6 transition-all duration-200 hover:shadow-card relative`}>
      {isEditable && !isEditMode && (
        <button
          onClick={handleEditClick}
          className="absolute top-4 right-4 p-2 rounded-md hover:bg-primary/10 transition-colors"
          aria-label={`Edit ${label}`}
        >
          <Icon name="Edit2" size={16} className="text-primary" />
        </button>
      )}
      <div className="flex items-start gap-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 flex-shrink-0">
          <Icon name={icon} size={24} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
          
          {isEditMode ? (
            <div className="space-y-3 mt-2">
              <div className="flex items-center gap-2">
                {prefix && <span className="text-xl font-semibold text-foreground">{prefix}</span>}
                <Input
                  type={inputType}
                  value={editValue}
                  onChange={(e) => {
                    setEditValue(e?.target?.value);
                    setError('');
                  }}
                  onKeyDown={handleKeyPress}
                  error={error}
                  className="text-xl font-semibold"
                  autoFocus
                />
                {suffix && <span className="text-xl font-semibold text-foreground">{suffix}</span>}
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  iconName="Check"
                  iconPosition="left"
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  iconName="X"
                  iconPosition="left"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-2xl font-semibold text-foreground mb-1">{value}</p>
              {description && (
                <p className="text-xs text-muted-foreground">{description}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialSummaryCard;